import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary dark:bg-primary-dark text-primary-foreground dark:text-primary-foreground-dark shadow hover:bg-primary/90 dark:hover:bg-primary-dark/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input dark:border-input-dark bg-background dark:bg-background-dark shadow-sm hover:text-foreground dark:hover:text-foreground-dark hover:border-foreground dark:hover:border-foreground-dark",
        "outline-primary":
          "border border-primary dark:border-primary-dark text-primary dark:text-primary-dark bg-background dark:bg-background-dark hover:bg-primary dark:hover:bg-primary-dark hover:text-primary-foreground dark:hover:text-primary-foreground-dark",
        secondary:
          "bg-secondary dark:bg-secondary-dark text-secondary-foreground dark:text-secondary-foreground-dark shadow-sm hover:bg-secondary/80 dark:hover:bg-secondary-dark/80",
        "outline-secondary":
          "border border-secondary dark:border-secondary-dark text-secondary dark:text-secondary-dark bg-background dark:bg-background-dark hover:bg-secondary dark:hover:bg-secondary-dark hover:text-secondary-foreground dark:hover:text-secondary-foreground-dark",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-muted-foreground dark:text-muted-foreground-dark underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
